import React, { FC, useEffect, useState } from 'react';
import { TextField, Autocomplete, Box } from '@mui/material';
import { Organization } from 'models/organization';
import { useLazyQuery } from '@apollo/client';
import { getChildClinicsNamesWithParentTemplate } from 'apollo/templates/organization/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';

type DeliveryClinicAutocompleteProps = {
    parentClinic: Partial<Organization>;
    deliveryClinic: Organization | null;
    /**
     * The clinic the lab has recorded as this dentist's default. Pre-selected when it
     * is among the options and nothing has been selected yet.
     */
    preferredClinicId?: string;
    onDeliveryClinicChange?: (clinic: Organization, isUserSelection: boolean) => void;
};
const DeliveryClinicAutocomplete: FC<DeliveryClinicAutocompleteProps> = ({
    parentClinic,
    deliveryClinic,
    preferredClinicId,
    onDeliveryClinicChange,
}) => {
    const { t: translate } = useTranslation(['orders']);
    const [selectedDeliveryClinic, setSelectedDeliveryClinic] = useState<Organization | null>(null);
    const [deliveryClinics, setDeliveryClinics] = useState<Organization[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();

    useEffect(() => {
        if (parentClinic) {
            fetchDeliveryClinics({
                variables: { parentClinicId: parentClinic._id, showOnlyDeliveryLocations: true },
            });
        }
    }, [parentClinic]);

    useEffect(() => {
        // Upstream uses `{}` as the "nothing picked yet" value, and `{}` is truthy, so a
        // real `_id` is what distinguishes an actual selection from a reset.
        setSelectedDeliveryClinic(deliveryClinic?._id ? (deliveryClinic as Organization) : null);
    }, [deliveryClinic]);

    // Pre-select the dentist's stored default clinic. Only applies while nothing is
    // selected, so it never overrides an explicit pick or an existing order's clinic.
    useEffect(() => {
        if (!preferredClinicId || deliveryClinic?._id || selectedDeliveryClinic?._id || !deliveryClinics.length) {
            return;
        }
        const preferredClinic = deliveryClinics.find((clinic) => clinic._id === preferredClinicId);
        if (preferredClinic) {
            setSelectedDeliveryClinic(preferredClinic);
            if (onDeliveryClinicChange) {
                onDeliveryClinicChange(preferredClinic, false);
            }
        }
    }, [preferredClinicId, deliveryClinics, deliveryClinic, selectedDeliveryClinic]);

    const [fetchDeliveryClinics] = useLazyQuery(getChildClinicsNamesWithParentTemplate, {
        onCompleted: async ({ childClinicsWithParent }) => {
            setDeliveryClinics(childClinicsWithParent);
            if (childClinicsWithParent.length === 1) {
                setSelectedDeliveryClinic(childClinicsWithParent[0]);
                if (onDeliveryClinicChange && selectedDeliveryClinic?._id !== childClinicsWithParent[0]._id) {
                    onDeliveryClinicChange(childClinicsWithParent[0], false);
                }
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleDeliveryClinicChange = async (clinic: Organization | null) => {
        setSelectedDeliveryClinic(clinic);
        if (clinic) {
            if (onDeliveryClinicChange && selectedDeliveryClinic?._id !== clinic._id) {
                onDeliveryClinicChange(clinic, true);
            }
        }
    };

    return (
        <Box>
            {deliveryClinics.length > 1 ? (
                <Autocomplete
                    autoFocus
                    autoComplete
                    disableClearable
                    options={deliveryClinics}
                    getOptionLabel={(clinic: Organization) => `${clinic.name || ''}`}
                    getOptionKey={(clinic: Organization) => clinic._id}
                    isOptionEqualToValue={(option: Organization, value: Organization) => option?._id === value?._id}
                    value={(selectedDeliveryClinic || { name: '' }) as Organization}
                    onChange={(_, value) => handleDeliveryClinicChange(value)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            required
                            label={translate('orders:DELIVERY_CLINIC')}
                            data-test="delivery-clinic-autocomplete.input"
                        />
                    )}
                />
            ) : undefined}
        </Box>
    );
};

export default DeliveryClinicAutocomplete;
